import axios from "axios";
import Swal from "sweetalert2";
import PKSOFT_URLS from "../utilities/Pk_api_Urls";
import {
  getCartItems,
  getWishlistItems,
  clearCart,
  clearWishlist,
} from "../utilities/cookieUtils";
import { getEncodedTimestamp } from "../utilities/dateUtils";

/**
 * Unified function to sync cart/wishlist items AND fetch counts via a single API call
 * Can be used in 2 modes:
 * 1. Sync mode: Pass userId, userType, and optionally c_products/w_products → syncs items and returns counts
 * 2. Count-only mode: Pass userId and userType without items → just fetches current counts
 * @param userId - User or Guest ID
 * @param userType - "user" | "guest" | "random"
 * @param c_products - optional: array of cart product IDs to sync
 * @param w_products - optional: array of wishlist product IDs to sync
 * @returns { cartCount, wishlistCount, isRandom }
 */
const _inFlightSyncs: Map<string, Promise<any>> = new Map();

export const syncCartWishlistWithCounts = async (
  userId: string,
  userType: "user" | "guest" | "random",
  c_products: string[] = [],
  w_products: string[] = []
) => {
  // Don't call API for random users
  if (userType === "random" || !userId) {
    return {
      cartCount: 0,
      wishlistCount: 0,
      isRandom: true,
    };
  }

  // Dedup key: include products in key to allow multiple calls with different items
  const key = `${userType}:${userId}:${c_products.length}:${w_products.length}`;
  if (_inFlightSyncs.has(key)) {
    return _inFlightSyncs.get(key);
  }

  const promise = (async () => {
    try {
      const uid = userId;
      const d = getEncodedTimestamp();

      // Build payload: if no products provided, it's a count-only request
      const payload = {

        c_mappings: c_products.length > 0 ? c_products : [],
        w_mappings: w_products.length > 0 ? w_products : [],
        date: d
      };

      // console.log(`[SYNC] Calling CHECKLIST API:`, { uid, payload });

      const response = await axios.post(
        `${PKSOFT_URLS.CUSTOMER.COUNT}/${uid}`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (
        response?.data?.messageCode === 100 &&
        response?.data?.status === 200
      ) {
        const cartCount = response.data?.data?.cartCount || 0;
        const wishlistCount = response.data?.data?.wishlistCount || 0;
        // console.log(`[SYNC] API response:`, { cartCount, wishlistCount });
        return {
          cartCount,
          wishlistCount,
          isRandom: false,
        };
      }

      // If API returned non-success, fall back
      console.warn("[SYNC] API returned non-success, using fallback counts");
      return {
        cartCount: 0,
        wishlistCount: 0,
        isRandom: false,
      };
    } catch (error) {
      console.error(`[SYNC] API call failed:`, error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to sync cart and wishlist.",
      });
      return {
        cartCount: 0,
        wishlistCount: 0,
        isRandom: false,
      };
    }
  })();

  // Save in-flight promise and cleanup when done
  _inFlightSyncs.set(key, promise);
  promise.finally(() => _inFlightSyncs.delete(key));
  return promise;
};

/**
 * Backward-compatible wrapper: sync items after login
 * Automatically gets items from cookies and calls syncCartWishlistWithCounts
 * @param userId - User or Guest ID
 * @param userType - "user" | "guest"
 * @returns { cartCount, wishlistCount, isRandom }
 */
export const syncCookieDataToBackend = async (
  userId: string,
  userType: "user" | "guest",
  manualCartItems?: any[],
  manualWishlistItems?: any[]
) => {
  const cartItems = manualCartItems || getCartItems();
  const wishlistItems = manualWishlistItems || getWishlistItems();

  // FIX: Extract mappingCode from cart items (ProductEntry)
  const c_products = Array.isArray(cartItems)
    ? cartItems.map((it: any) => String(it.mappingCode || it.productId))
    : [];

  // FIX: Wishlist items are already strings (mappingCodes)
  const w_products = Array.isArray(wishlistItems)
    ? wishlistItems.map((it: any) => typeof it === 'string' ? it : String(it.mappingCode || it))
    : [];

  // console.log(`[SYNC] Syncing ${userType} cart & wishlist:`, {
  //   cartItems: c_products.length,
  //   wishlistItems: w_products.length,
  //   manualCart: !!manualCartItems,
  //   manualWishlist: !!manualWishlistItems,
  // });

  const result = await syncCartWishlistWithCounts(userId, userType, c_products, w_products);

  // Clear local cookies after successful sync
  if (!result.isRandom) {
    clearLocalCartWishlistCookies();
  }

  return result;
};

/**
 * Backward-compatible wrapper: fetch counts only
 * Calls syncCartWishlistWithCounts with empty products array
 * @param userId - User or Guest ID
 * @param userType - "user" | "guest" | "random"
 * @returns { cartCount, wishlistCount, isRandom }
 */
export const fetchCartWishlistCounts = async (
  userId: string,
  userType: "user" | "guest" | "random"
) => {
  return syncCartWishlistWithCounts(userId, userType, [], []);
};

export const clearLocalCartWishlistCookies = () => {
  try {
    // For authenticated users, we don't need cookies anymore
    // Backend now manages the data
    clearCart();
    clearWishlist();
    // console.log("[SYNC] Cookie data synced to backend - local cookies cleared");
  } catch (error) {
    // console.error("[SYNC] Error clearing cookies:", error);
  }
};

export default {
  syncCookieDataToBackend,
  fetchCartWishlistCounts,
  clearLocalCartWishlistCookies,
};
